import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckupRequestV2Response, CheckupRequestInclude } from '@companion-professional/dctypes';
import { getCheckupRequest } from '../lib/api';

export type CheckupUrlParams = {
  // checkupId comes from the URL path. e.g. https://https://checkup.companionpro.biz/:checkupId
  checkupId: string;
};

export const useCheckupRequest = (checkupId: string | null) => {
  const [checkupComplete, setCheckupComplete] = useState<boolean>(false);

  const {
    isPending,
    error,
    data: checkupRequestv2Response
  } = useQuery<CheckupRequestV2Response | null>({
    queryKey: ['checkupRequest', checkupId],

    queryFn: async () => {
      if (!checkupId || checkupId === '') {
        throw new Error('Invalid Checkup ID');
      }
      // Currently - just passing all the params just to ensure backward compatibility.
      // Once custom lander is matured, I can sift through the code and improve on this.
      const checkup = await getCheckupRequest(checkupId, [
        CheckupRequestInclude.TYPE,
        CheckupRequestInclude.VET,
        CheckupRequestInclude.PET,
        CheckupRequestInclude.STATUS,
        CheckupRequestInclude.ITEMS,
        CheckupRequestInclude.CLINIC
      ]);

      setCheckupComplete(checkup.status === 'complete');
      return checkup;
    },

    // Setting the stale time to 30 seconds so that we're not constantly fetching the same data from the server.  We
    // might want to increase this value depending on the user's behavior (e.g. if they hang out on the landing page
    // for over 30 seconds, and we expect that the data will most likely stay the same, we might want to increase the
    // time).
    staleTime: 1000 * 30, // 30 seconds

    // Only fetch if we have a checkupId (prevents fetching during redirect if we're cleaning up the checkup id)
    enabled: !!checkupId
  });

  return {
    isPending,
    error,
    checkupRequestv2Response,
    checkupComplete
  };
};

